export const normalizeIdentityType = (identityType: string) => {
  return identityType.toLowerCase();
};

/**
 * Normalizes identity types in API response objects that contain identityTypes array.
 * Each identity object should have a 'name' property that will be normalized.
 *
 * @param responseData - The response data object that may contain identityTypes
 * @returns The response data with normalized identity types, or the original data if no identityTypes found
 */
export const normalizeIdentityTypesInResponse = <
  T extends Record<string, unknown>,
>(
  responseData: T
): T => {
  // Check if response has data.identityTypes structure (like LoginMfaRequiredResponseDto)
  if (
    responseData &&
    typeof responseData === 'object' &&
    'data' in responseData &&
    responseData.data &&
    typeof responseData.data === 'object' &&
    'identityTypes' in responseData.data &&
    Array.isArray(responseData.data.identityTypes)
  ) {
    const data = responseData.data as {
      identityTypes: Array<{ name: string; isEnabled?: boolean }>;
      [key: string]: unknown;
    };

    return {
      ...responseData,
      data: {
        ...data,
        identityTypes: data.identityTypes.map(identity => ({
          ...identity,
          name: normalizeIdentityType(identity.name),
        })),
      },
    } as T;
  }

  // Check if response has identityTypes directly (array of objects)
  if (
    responseData &&
    typeof responseData === 'object' &&
    'identityTypes' in responseData &&
    Array.isArray(responseData.identityTypes)
  ) {
    const identityTypes = responseData.identityTypes as Array<{
      name: string;
      isEnabled?: boolean;
    }>;

    return {
      ...responseData,
      identityTypes: identityTypes.map(identity => ({
        ...identity,
        name: normalizeIdentityType(identity.name),
      })),
    } as T;
  }

  return responseData;
};
